import {
  type ReactNode,
  type ReactElement,
  type ComponentType,
  useRef,
  useState,
  useCallback,
} from "react";
import { HardHatIcon } from "@phosphor-icons/react/HardHat";
import { CaretLeftIcon } from "@phosphor-icons/react/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import type { IconWeight } from "@phosphor-icons/react/dist/lib/types";
import type { DialogBaseProps } from "../Dialog/DialogBaseProps";
import { ConfirmDialog } from "../Dialog/Internal/ConfirmDialog";
import {
  type ButtonRoleKey,
  standardStyles,
  linkStyles,
  sizeStyles,
  disabledStyles,
  toneIconColors,
} from "./Internal/buttonStyles";
import { useIsInMenu } from "../Menu/Internal/MenuContext";
import { useControlBarSlot } from "../PageLayout/Internal/ControlBarContext";
import { useIsInTable } from "../Table/Internal/TableContext";
import { useIsInBanner } from "../Display/Internal/BannerContext";
import { useIsInDialogHeader } from "../Dialog/Internal/DialogHeaderContext";
import { useInsideDialog } from "../Dialog/Internal/InsideDialogContext";
import { DialogMount_ } from "./Internal/DialogMount";
import { MenuItemButton_ } from "./Internal/MenuItemButton";
import { IconActionButton_ } from "./Internal/IconActionButton";
import { ParkedShakeWrapper_ } from "./Internal/ParkedShakeWrapper";
import {
  useDialogTrayHasEntry,
  useDialogTrayMutatorsOptional,
} from "../Dialog/DialogTrayProvider";
import { Spinner } from "../Feedback/Spinner";
import { headingFontStyle } from "../Theme/headingFontStyle";
import { resolveConfirmConfig, type ConfirmConfig } from "./confirmConfig";
type ButtonSize = "default" | "compact" | "large" | "link";
export type ButtonTone = "neutral" | "caution" | "danger";
type DisabledState =
  | { disabled?: false | undefined; disabledMessage?: never }
  | {
      disabled: boolean;
      /** Explains why the action is unavailable. Shown on hover. */ disabledMessage: string;
    };

interface ButtonBaseProps {
  /** How much space the button takes. "link" renders as inline text. */
  size?: ButtonSize | undefined;
  primary?: boolean | undefined;
  tone?: ButtonTone | undefined;
  icon?: ComponentType<{ className?: string }> | undefined;
  iconWeight?: IconWeight | undefined;
  /** Leading "‹" chevron for a "Previous" step button (fills the leading icon slot). */
  previousArrow?: boolean | undefined;
  /** Trailing "›" chevron for a "Next" step button. */
  nextArrow?: boolean | undefined;
  children?: ReactNode;
  onClick?: ((input?: string) => void | Promise<void>) | undefined;
  href?: string | undefined;
  loading?: boolean | undefined;
  /**
   * Post-resolution status for the async action this button drives: after
   * `loading` clears, show a green ✓ (`loadingSuccess`) or red ✗
   * (`loadingFailure`) in the leading icon slot so the outcome is visible on
   * the button itself (e.g. a customs-docs upload). Suppressed while
   * `loading` is set; an explicit `icon` is overridden while active.
   */
  loadingSuccess?: boolean | undefined;
  loadingFailure?: boolean | undefined;
  /**
   * Link mode only: inherit font size and weight from the surrounding text
   * instead of the default text-xs/semibold. For links that flow inside body
   * copy (or a styled row) and should read at that copy's size.
   */
  inheritFont?: boolean | undefined;
  /**
   * Stretch to fill the container's width — for a full-width CTA (e.g. a login
   * "Sign In" button). Off by default; the button shrink-wraps its label.
   * Distinct from `display="full"`, which is about showing the label at all.
   */
  fullWidth?: boolean | undefined;
  tooltip?: ReactNode;
  truncate?: boolean | undefined;
  dialog?: (() => ReactElement<DialogBaseProps>) | undefined;
  /**
   * Opt the dialog into the tray. When true and a `DialogTrayProvider` is
   * in scope, clicking the button registers the dialog with the tray
   * (rendered above the router) instead of mounting it locally — so the
   * dialog survives navigation and can be parked. When no provider is in
   * scope, falls back to the normal local mount with a one-time dev warning.
   * Ignored when `dialog` is not set.
   */
  tray?: boolean;
  /**
   * Stable identity for the parked dialog. When set, clicking the button
   * a second time (even after navigation remounts the button) restores
   * the existing tray entry instead of registering a duplicate. The
   * activeTrayKey-based disable below is a same-page UX hint; this prop
   * is the actual cross-navigation safety net — without it, open → park
   * → navigate → return → click would silently produce two editors for
   * the same entity. Recommended shape: `${kind}:${entityId}` (e.g.
   * `pickup-address:abc-123`).
   */
  dedupeKey?: string | undefined;
  confirm?: string | ConfirmConfig | undefined;
  /**
   * Manual override for how the button presents its label, regardless of the
   * surface it sits in:
   *  - `"auto"` (default) — the surrounding context decides (e.g. table rows
   *    collapse icon buttons to icon-only).
   *  - `"full"` — always icon + label, even where the context would collapse.
   *  - `"icon"` — always icon-only (the label becomes the hover tooltip).
   *    Pair with `icon`, like `iconOnly`.
   * Escape hatch for when a context adaptation guesses wrong — prefer fixing
   * the surface (PortalContextReset) when the context itself is leaking.
   * Not honored inside an ActionBar (the bar owns expansion) or a Menu
   * (items always show their label).
   */
  display?: "auto" | "full" | "icon" | undefined;
  /** Forces icon-only. Equivalent to `display="icon"`; prefer `display`. */
  iconOnly?: boolean | undefined;
  wip?: boolean | undefined;
  /**
   * Render as a compact, borderless, muted icon action (icon + tooltip) — the
   * style formerly owned by DialogActionButton. Works anywhere and coexists
   * with bordered buttons. Auto-enabled inside a Dialog header-actions slot, so
   * header actions don't need to pass it. Pair with `icon` + `tooltip`.
   */
  iconAction?: boolean | undefined;
  /**
   * Trailing count badge (renders only when > 0). On an icon action it sits
   * next to the icon, typically paired with `color`. On a standard button it
   * renders as a small pill after the label, e.g. the active-filter count on a
   * "Filters" button.
   */
  badge?: number | undefined;
  /**
   * Visible text shown next to the icon of an icon action — same flat,
   * borderless style, just no longer icon-only. Use it when the action needs to
   * be discoverable rather than relying on the hover tooltip (e.g. "Add todo" in
   * a dialog header). Only honored for icon actions, like `badge` and `color`.
   * When set it also serves as the accessible name, so a separate `tooltip` is
   * redundant.
   */
  label?: string | undefined;
  /**
   * Tailwind text-color class that tints the icon (e.g. `text-blue-text`). Only
   * honored for icon actions, where it emphasizes one action against the
   * default muted color. Ignored elsewhere.
   */
  color?: string | undefined;
  /** data-testid passed through to the rendered button element. */
  "data-testid"?: string | undefined;
}

export type ButtonProps = ButtonBaseProps & DisabledState;
function useButtonConfirmation_(
  onClick?: (input?: string) => void | Promise<void>,
) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const handleConfirm = useCallback(
    async (input?: string) => {
      setConfirmLoading(true);
      try {
        await onClick?.(input);
      } finally {
        setConfirmLoading(false);
        setConfirmOpen(false);
      }
    },
    [onClick],
  );
  return {
    dialogOpen,
    setDialogOpen,
    confirmOpen,
    setConfirmOpen,
    confirmLoading,
    handleConfirm,
  };
}
export function Button(props: ButtonProps) {
  const isInMenu = useIsInMenu();
  const isInDialogHeader = useIsInDialogHeader();
  if (isInMenu) {
    return <MenuItemButton_ {...props} />;
  }
  // Icon action: explicit `iconAction`, or auto inside a Dialog header so
  // header actions render compact without repeating the prop. (IconActionButton_
  // reads the dialog-header context itself to pick its resting tint.)
  if (props.iconAction || isInDialogHeader) {
    return <IconActionButton_ {...props} />;
  }
  return <StandardButton_ {...props} />;
}
// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
function StandardButton_({
  size = "default",
  primary: isPrimary,
  tone = "neutral",
  icon: IconProp,
  iconWeight,
  previousArrow,
  nextArrow,
  children,
  onClick,
  href,
  disabled,
  disabledMessage,
  loading,
  loadingSuccess,
  loadingFailure,
  inheritFont,
  fullWidth,
  tooltip,
  truncate,
  dialog,
  tray,
  dedupeKey,
  confirm,
  display = "auto",
  iconOnly,
  wip,
  badge,
  "data-testid": dataTestid,
}: ButtonProps) {
  const {
    dialogOpen,
    setDialogOpen,
    confirmOpen,
    setConfirmOpen,
    confirmLoading,
    handleConfirm,
  } = useButtonConfirmation_(onClick);
  // Tray opt-in: when `tray` is set AND a provider is in scope, clicking
  // routes through the tray instead of the local DialogMount_ path.
  // No provider + tray=true → fall back to local mount with a one-time
  // dev warning (see warnedRef in handleClick). We subscribe to MUTATORS
  // ONLY (not entries) so Button doesn't broadcast-rerender every time a
  // tray dialog opens or closes — that broadcast plus Radix Tooltip's ref
  // bookkeeping is a documented cause of "Maximum update depth exceeded".
  const trayCtx = useDialogTrayMutatorsOptional();
  // Tray-by-default: when a DialogTrayProvider is in scope, every
  // Button-driven dialog routes through the tray, regardless of whether
  // the caller passed `tray`. The `tray` prop guard was dropped as part
  // of ENG-4836 when parking-by-default became the intended shape — peek
  // is the explicit opt-out now, expressed on the Dialog primitive. Don't
  // reintroduce the `tray` guard without coordinating with the tray
  // rollout (`MenuItemButton_.tsx` mirrors this same line).
  const useTray = !!(dialog && trayCtx);
  const warnedRef = useRef(false);
  // Track the active tray entry opened by THIS button so we can disable the
  // button while the entry is alive. Without this, clicking a tray button
  // twice opens two copies of the same dialog — nonsense for an editor.
  // The provider invokes the onClose callback exactly once when the entry
  // is removed (X on chip, save-and-close, Clear-all), reactivating us.
  const [activeTrayKey, setActiveTrayKey] = useState<string | undefined>(
    undefined,
  );
  // Same-page guard: activeTrayKey survives renders but dies on unmount.
  // Cross-navigation guard: when the caller passes a `dedupeKey`, also
  // ask the provider whether an entry with that key exists. The provider
  // lives above the Router so this check survives the Button's remount
  // after navigation away and back — without it, a parked dialog's
  // trigger button would re-enable on every page revisit and let the
  // operator open a second copy. Buttons without `dedupeKey` keep the
  // mutators-only path (no entries subscription, no extra renders).
  const trayHasMatchingEntry = useDialogTrayHasEntry(dedupeKey);
  const isParked = !!activeTrayKey || trayHasMatchingEntry;
  const isDisabled = disabled || loading || wip || isParked;
  const wipTooltip = wip
    ? typeof children === "string"
      ? `${children} — Under construction`
      : "Under construction"
    : undefined;
  // Parked takes precedence — it's the most actionable hint ("you already
  // have one open, go find the chip"). Falls through to the existing
  // disabledMessage / iconOnly / wip / tooltip chain otherwise.
  const parkedTooltip = isParked
    ? "Already parked — click the chip on the right to resume."
    : undefined;
  const controlBarSlot = useControlBarSlot();
  const isInControlBar = !!controlBarSlot;
  const isInTable = useIsInTable();
  const isInBanner = useIsInBanner();
  const insideDialog = useInsideDialog();
  // In a table, row-action buttons collapse to an icon + tooltip (the label
  // becomes the hover tooltip) to save the horizontal space a full label eats in
  // a dense row. Only when the button actually has an icon — a label-only table
  // button has nothing to collapse to, so it keeps its label.
  // `display` is the manual override: "icon"/"full" win over every
  // context-driven guess; "auto" (default) keeps the adaptive behavior.
  const effectiveIconOnly =
    display === "icon" ||
    (display !== "full" && (iconOnly || (isInTable && !!IconProp)));
  // Buttons in a plain ControlBar / ActionBar show their icon + label. When
  // collapsed to icon-only (a table row, `display="icon"`), the label becomes
  // the hover tooltip.
  const effectiveTooltip =
    parkedTooltip ??
    (disabled && disabledMessage
      ? disabledMessage
      : effectiveIconOnly && !tooltip
        ? children
        : wip && !tooltip
          ? wipTooltip
          : tooltip);
  // Control bar / table / banner overrides: force compact size
  const effectiveSize =
    isInControlBar || isInTable || isInBanner ? "compact" : size;
  // Control bars are all-secondary by design (header action rows have no single
  // "main" action), so `primary` is intentionally ignored inside one — passing
  // it is a no-op, not a bug. Make the primary action a footer/onOk button, or
  // hoist it out of the bar. (Taxonomy: see the Button Types doc.)
  const effectiveRole: ButtonRoleKey = isInControlBar
    ? "secondary"
    : isPrimary
      ? "primary"
      : "secondary";
  const isLink = effectiveSize === "link";
  const base = isLink
    ? `inline-flex items-center gap-1.5 ${
        inheritFont ? "" : "text-xs font-semibold"
      } transition-colors whitespace-nowrap cursor-pointer`
    : // A full-width CTA (e.g. a login "Sign In") carries larger label text so
      // it reads as the primary action, not a dense inline control. The
      // "large" size (prominent standalone CTAs like Select Quote) reads at
      // text-sm — between the dense default and the full-width hero.
      `group inline-flex items-center gap-1.5 rounded-md ${fullWidth ? "text-base" : effectiveSize === "large" ? "text-sm" : "text-xs"} font-semibold transition-all whitespace-nowrap cursor-pointer hover:scale-105 active:scale-95`;
  const sizeClass = isLink
    ? ""
    : sizeStyles[effectiveSize as "default" | "compact" | "large"];
  const styleClass = isLink
    ? linkStyles[tone]
    : standardStyles[effectiveRole][tone];
  // The design reduced the boxed control height to 26px (see textInputStyles /
  // Dropdown), so control-bar buttons match at h-[26px] — otherwise they tower
  // over the sibling search/filter inputs.
  const controlBarStyle = isInControlBar ? "h-[26px]" : "";
  // A full-width CTA fills its container and centers its label; ignored for
  // link buttons, which are inline text.
  const fullWidthClass = fullWidth && !isLink ? "w-full justify-center" : "";
  const combined = `${base} ${sizeClass} ${styleClass} ${isDisabled ? disabledStyles : ""} ${controlBarStyle} ${fullWidthClass}${truncate ? " max-w-full min-w-0" : ""}`;
  // Button labels render on the brand headline face (Neue Haas Unica), matching
  // headings. Link-mode buttons are inline text that flows with surrounding body
  // copy, so they stay on the body font.
  const fontStyle = isLink ? undefined : headingFontStyle();
  // Secondary non-neutral buttons: icon gets the tone color, text stays foreground
  const secondaryIconColor =
    effectiveRole === "secondary" && tone === "caution"
      ? "text-orange"
      : effectiveRole === "secondary" && tone === "danger"
        ? "text-red"
        : undefined;
  // Icon hover scale: a touch on ActionBar buttons (the "icon pop" on top of
  // the button's own scale), a bigger pop elsewhere. Skipped for icon-only
  // (the button already scales) and link buttons. Uses effectiveIconOnly (not
  // the raw iconOnly prop) so every icon-only render — explicit prop,
  // display="icon", or the in-table auto-collapse — consistently skips the
  // redundant icon pop instead of double-scaling with the button.
  const iconHoverClass =
    isLink || effectiveIconOnly
      ? ""
      : "transition-transform duration-150 group-hover:scale-125";
  // "Previous" step buttons fill the leading icon slot with a ‹ chevron; an
  // explicit icon still wins. Bold, not the Icon default of duotone: Phosphor
  // draws a duotone caret as a filled triangle with an outline, which on a
  // Next/Previous button read as a media play button rather than a direction.
  const leadingIcon = IconProp ?? (previousArrow ? CaretLeftIcon : undefined);
  const stepArrowWeight = "bold" as const;
  // Post-resolution ✓/✗ only paint once the spinner is gone — while loading,
  // the spinner wins so a retry doesn't show a stale verdict next to it.
  const isPostFailure = !!loadingFailure && !loading;
  const isPostSuccess = !!loadingSuccess && !loading;
  const iconElement = loading ? (
    <span className={iconHoverClass}>
      <Spinner size={16} onColor />
    </span>
  ) : isPostFailure ? (
    <span className={iconHoverClass}>
      <Icon icon={XIcon} size="md" color="text-red" />
    </span>
  ) : isPostSuccess ? (
    <span className={iconHoverClass}>
      <Icon icon={CheckIcon} size="md" color="text-green" />
    </span>
  ) : leadingIcon ? (
    <span className={iconHoverClass}>
      <Icon
        icon={leadingIcon}
        size="md"
        {...(iconWeight
          ? { weight: iconWeight }
          : previousArrow && !IconProp
            ? { weight: stepArrowWeight }
            : {})}
        {...(secondaryIconColor ? { color: secondaryIconColor } : {})}
      />
    </span>
  ) : undefined;
  const wipBadge = wip ? (
    <Icon icon={HardHatIcon} size="xs" weight="fill" color="text-yellow-500" />
  ) : undefined;
  // A small trailing count pill (e.g. active-filter count on a "Filters"
  // button). Only shows when positive; a zero count is simply no badge.
  const countBadge =
    badge !== undefined && badge > 0 ? (
      <span className="ml-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-blue px-1 py-px text-center text-[10px] leading-tight font-extrabold text-white">
        {badge}
      </span>
    ) : undefined;
  const wipOverlay = wip ? (
    <span className="absolute -top-1 -right-1">
      <Icon
        icon={HardHatIcon}
        size="xs"
        weight="fill"
        color="text-yellow-500"
      />
    </span>
  ) : undefined;
  // icon-only render: a compact icon with the label kept in the DOM as an
  // `sr-only` span. The label takes no visual space but stays in the
  // accessibility tree (screen readers announce it) and is reachable by tests —
  // sighted users get it via the tooltip on hover. Otherwise icon + label.
  const content = effectiveIconOnly ? (
    <>
      {wip ? (
        <span className="relative">
          {iconElement}
          {wipOverlay}
        </span>
      ) : (
        iconElement
      )}
      {children && <span className="sr-only">{children}</span>}
    </>
  ) : (
    <>
      {iconElement}
      {truncate ? (
        <span className="min-w-0 truncate">{children}</span>
      ) : (
        children
      )}
      {countBadge}
      {nextArrow && (
        <span className={iconHoverClass}>
          <Icon icon={CaretRightIcon} size="md" weight={stepArrowWeight} />
        </span>
      )}
      {wipBadge}
    </>
  );
  const handleClick = () => {
    if (isDisabled) return;
    if (confirm) {
      setConfirmOpen(true);
      return;
    }
    if (dialog) {
      if (tray && !trayCtx && !warnedRef.current) {
        console.warn(
          "Button has tray={true} but no DialogTrayProvider in scope — falling back to local dialog mounting.",
        );
        warnedRef.current = true;
      }
      if (useTray) {
        const handle = trayCtx.openElement(dialog(), {
          onClose: () => setActiveTrayKey(undefined),
          ...(dedupeKey ? { dedupeKey } : {}),
          // Nested dialogs: park the chip above the parent overlay (ENG-5878
          // follow-up / nested-dialog fix), else it hides behind it.
          ...(insideDialog ? { chipAboveModals: true } : {}),
        });
        setActiveTrayKey(handle.key);
      } else {
        setDialogOpen(true);
      }
    }
    onClick?.();
  };
  const confirmConfig: ConfirmConfig | undefined =
    resolveConfirmConfig(confirm);
  // External links (anything that isn't a same-origin Hubble route) open
  // in a new tab by default — we're routing the user off our app and
  // almost always want to preserve the current tab. Relative hrefs
  // (starting with "/") and in-page fragments stay in the same tab.
  const isExternal = !!href && /^[a-z]+:\/\//i.test(href);
  const button = href ? (
    <a
      href={isDisabled ? undefined : href}
      className={combined}
      style={fontStyle}
      aria-disabled={isDisabled || undefined}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
    >
      {content}
    </a>
  ) : (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={combined}
      style={fontStyle}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
    >
      {content}
    </button>
  );
  const buttonWithTooltip = effectiveTooltip ? (
    // `stretch` so a full-width button keeps its width when it gains a tooltip
    // — most often a DISABLED one, since `disabledMessage` always brings a
    // tooltip with it. Without it the wrapper shrink-wraps and the button
    // collapses to its label.
    <Tooltip
      content={effectiveTooltip}
      stretch={fullWidth === true}
      {...(truncate ? { block: true } : {})}
    >
      {button}
    </Tooltip>
  ) : (
    button
  );
  // When the button is parked, wrap in ParkedShakeWrapper_ so clicking the
  // (visually disabled) area gives kinetic feedback — button rattles, a
  // padlock pops in to the left, then both fade out. Lets the operator
  // FEEL that the action is blocked instead of guessing why nothing
  // happened. Skipped for non-parked disabled states (loading / wip /
  // explicit disabled) — those have their own affordances (spinner /
  // hardhat / explanatory disabledMessage tooltip).
  const finalButton = isParked ? (
    <ParkedShakeWrapper_>{buttonWithTooltip}</ParkedShakeWrapper_>
  ) : (
    buttonWithTooltip
  );
  if (confirm && confirmConfig) {
    return (
      <>
        {finalButton}
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmLabel={confirmConfig.confirmLabel ?? "Confirm"}
          cancelLabel={confirmConfig.cancelLabel}
          confirmTone={tone}
          icon={confirmConfig.icon}
          iconColors={toneIconColors[confirmConfig.iconTone ?? tone]}
          loading={confirmLoading}
          inputLabel={confirmConfig.inputLabel}
          inputPlaceholder={confirmConfig.inputPlaceholder}
          inputDefaultValue={confirmConfig.inputDefaultValue}
        />
      </>
    );
  }
  // When routing through the tray, the local DialogMount_ never renders —
  // the dialog lives in the tray host's tree. The handle's key is tracked
  // in activeTrayKey above so the button stays disabled until the entry
  // closes (the provider invokes our onClose callback then).
  if (dialog && !useTray) {
    return (
      <>
        {finalButton}
        <DialogMount_
          dialog={dialog}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </>
    );
  }
  return finalButton;
}
